// Data sources, signal catalog and the QuerySpec (docs 03 §3, 04, 05).
import { z } from 'zod';
import { objectIdSchema } from './common';

// ---- Identifier rules (doc 04 §4.3–4.4): validated when a signal is saved ----
/** Influx bucket / measurement / field / tag keys and values. */
export const influxIdentifierSchema = z.string().regex(/^[\w .:/-]{1,128}$/, 'Only letters, digits, space and . : / _ - are allowed (max 128)');
/** Mongo collection names and dotted field paths: no `$`, no operators, no empty segments. */
export const mongoPathSchema = z
  .string()
  .regex(/^[A-Za-z_][\w-]{0,63}(\.[A-Za-z_][\w-]{0,63}){0,5}$/, 'Use a field path like "meta.well" (letters, digits, _ and -, up to 6 levels)');
const metaValue = z.union([z.string().max(128).regex(/^[^$\0]*$/), z.number(), z.boolean()]);

// ---- Data sources ----
export const DATASOURCE_KINDS = ['influxdb2', 'mongodb'] as const;
export const dataSourceLimitsSchema = z.object({
  maxConcurrent: z.number().int().min(1).max(32).default(4),
  maxPointsPerSeries: z.number().int().min(100).max(5000).default(5000),
});
const timeoutMs = z.number().int().min(1000).max(120_000).default(15_000);

export const influxConfigSchema = z.object({
  url: z.string().url().max(500),
  org: z.string().min(1).max(128),
  /** The client's bucket. When set, discovery reads only this bucket. */
  defaultBucket: z.string().max(128).nullable().optional(),
  /** Tag holding the field name, and tag holding the well name, of each series. */
  fieldTag: influxIdentifierSchema.default('field'),
  wellTag: influxIdentifierSchema.default('well'),
  timeoutMs,
});
export const mongoConfigSchema = z.object({
  database: z.string().regex(/^[\w-]{1,63}$/, 'Database names use letters, digits, _ and -'),
  timeoutMs,
  readPreference: z.enum(['primary', 'primaryPreferred', 'secondary', 'secondaryPreferred', 'nearest']).default('secondaryPreferred'),
  /** Document paths holding the field name and the well name of each series. */
  fieldTag: mongoPathSchema.default('meta.field'),
  wellTag: mongoPathSchema.default('meta.well'),
});
/** Defaults for a data source saved before the tag settings existed. */
export const ASSET_TAG_DEFAULTS = { influxdb2: { fieldTag: 'field', wellTag: 'well' }, mongodb: { fieldTag: 'meta.field', wellTag: 'meta.well' } } as const;

const dataSourceName = z.string().trim().min(1).max(120);
/** Create: secrets are required. They are write-only and never come back. */
export const createDataSourceRequestSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('influxdb2'),
    name: dataSourceName,
    config: influxConfigSchema,
    secrets: z.object({ token: z.string().min(1).max(1000) }),
    limits: dataSourceLimitsSchema.partial().optional(),
  }),
  z.object({
    kind: z.literal('mongodb'),
    name: dataSourceName,
    config: mongoConfigSchema,
    secrets: z.object({ uri: z.string().min(10).max(2000) }),
    limits: dataSourceLimitsSchema.partial().optional(),
  }),
]);
export type CreateDataSourceRequest = z.infer<typeof createDataSourceRequestSchema>;

export const updateDataSourceRequestSchema = z.object({
  name: dataSourceName.optional(),
  config: z.union([influxConfigSchema.partial(), mongoConfigSchema.partial()]).optional(),
  /** Omit to keep the stored secret ("••••• (set)"). */
  secrets: z.object({ token: z.string().min(1).max(1000).optional(), uri: z.string().min(10).max(2000).optional() }).optional(),
  limits: dataSourceLimitsSchema.partial().optional(),
  paused: z.boolean().optional(),
});
export type UpdateDataSourceRequest = z.infer<typeof updateDataSourceRequestSchema>;

export const dataSourceStatusSchema = z.enum(['ok', 'error', 'paused', 'unknown']);
export const lastCheckSchema = z.object({ at: z.string(), ok: z.boolean(), latencyMs: z.number(), error: z.string().nullable() });
export const dataSourceSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  kind: z.enum(DATASOURCE_KINDS),
  status: dataSourceStatusSchema,
  /** Present only for callers with `datasources.manage`; Editors get name/kind/status. */
  config: z.record(z.unknown()).nullable(),
  limits: dataSourceLimitsSchema.nullable(),
  lastCheck: lastCheckSchema.nullable(),
  /** Which secrets are set — never their values. */
  secretsSet: z.array(z.string()),
  signalCount: z.number().nullable(),
});
export type DataSource = z.infer<typeof dataSourceSchema>;

export const testConnectionResultSchema = z.object({
  ok: z.boolean(),
  latencyMs: z.number(),
  version: z.string().nullable(),
  error: z.string().nullable(),
});
export type TestConnectionResult = z.infer<typeof testConnectionResultSchema>;

// ---- Discovery ----
export const discoverRequestSchema = z.object({
  lookbackDays: z.number().int().min(1).max(365).default(30),
  /** Mongo: dimension paths to break series down by (e.g. `meta.well`). Defaults to the meta field's keys. */
  mongoDimensions: z.array(mongoPathSchema).max(8).optional(),
  /** Mongo regular collections: time field per collection. */
  mongoTimeFields: z.record(z.string().max(64), mongoPathSchema).optional(),
});
export type DiscoverRequest = z.infer<typeof discoverRequestSchema>;

export const discoveredSeriesSchema = z.object({
  /** Stable id of the raw series inside its data source (used to mark "already cataloged"). */
  id: z.string(),
  /** bucket (Influx) or collection (Mongo). */
  container: z.string(),
  /** measurement (Influx); empty for Mongo. */
  measurement: z.string(),
  /** field key (Influx) or value path (Mongo). */
  field: z.string(),
  /** tags (Influx) or meta filter (Mongo). */
  tags: z.record(z.union([z.string(), z.number(), z.boolean()])),
  valueType: z.enum(['number', 'boolean', 'string']),
  lastValue: z.union([z.number(), z.boolean(), z.string(), z.null()]),
  lastAt: z.string().nullable(),
  intervalSec: z.number().nullable(),
  /** Mongo only: the collection's time field. */
  timeField: z.string().nullable(),
  /** Field and well names read from the data source's field / well tags (null when absent). */
  place: z.object({ field: z.string().nullable(), well: z.string().nullable() }).default({ field: null, well: null }),
});
export type DiscoveredSeries = z.infer<typeof discoveredSeriesSchema>;

export const discoveryResultSchema = z.object({
  dataSourceId: objectIdSchema,
  at: z.string(),
  lookbackDays: z.number(),
  series: z.array(discoveredSeriesSchema),
  /** Every bucket (Influx) or collection (Mongo) read, also those with no series in the look-back period. */
  containers: z.array(z.string()).default([]),
  warnings: z.array(z.string()),
});
export type DiscoveryResult = z.infer<typeof discoveryResultSchema>;

export const jobSchema = z.object({
  id: z.string(),
  kind: z.string(),
  status: z.enum(['waiting', 'active', 'completed', 'failed']),
  progress: z.number(),
  message: z.string().nullable(),
  result: z.unknown().optional(),
  error: z.string().nullable(),
});
export type JobStatus = z.infer<typeof jobSchema>;

// ---- Signals ----
export const influxSourceSchema = z.object({
  bucket: influxIdentifierSchema,
  measurement: influxIdentifierSchema,
  field: influxIdentifierSchema,
  tags: z.record(influxIdentifierSchema, influxIdentifierSchema).default({}),
});
export const mongoSourceSchema = z.object({
  collection: z.string().regex(/^[A-Za-z_][\w.-]{0,119}$/, 'Invalid collection name'),
  timeField: mongoPathSchema,
  valueField: mongoPathSchema,
  metaFilter: z.record(mongoPathSchema, metaValue).default({}),
});
export type InfluxSource = z.infer<typeof influxSourceSchema>;
export type MongoSource = z.infer<typeof mongoSourceSchema>;

export const signalKeySchema = z.string().regex(/^[a-z0-9][a-z0-9._-]{1,79}$/, 'Use lowercase letters, digits, dots, dashes (e.g. "w101.whp")');
const range = z.object({ min: z.number(), max: z.number() }).refine((r) => r.max > r.min, 'max must be greater than min');

export const signalLinksSchema = z.object({
  fieldId: objectIdSchema,
  wellId: objectIdSchema,
  equipmentInstanceId: objectIdSchema.nullable().default(null),
  signalRole: z.string().max(48).nullable().default(null),
});

/** A calculated signal (doc 03 `calc`): an expression over other signals of the organization. */
export const calcSchema = z.object({
  expression: z.string().trim().min(1).max(500),
  /** Name used in the expression → the signal it reads (in that signal's storage unit). */
  inputs: z
    .record(z.string().regex(/^[a-z][a-z0-9_]{0,23}$/, 'Use a short lowercase name such as "qo"'), objectIdSchema)
    .refine((v) => Object.keys(v).length >= 1 && Object.keys(v).length <= 8, 'A calculation reads 1 to 8 signals'),
});
export type SignalCalc = z.infer<typeof calcSchema>;

export const calcSignalInputSchema = z.object({
  key: signalKeySchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  quantityKey: z.string().max(64),
  /** Unit of the number the expression produces. */
  storageUnitId: z.string().max(64),
  displayUnitId: z.string().max(64).nullable().default(null),
  decimals: z.number().int().min(0).max(8).nullable().default(null),
  links: z.object({ wellId: objectIdSchema }),
  clientVisible: z.boolean().default(false),
  calc: calcSchema,
});
export type CalcSignalInput = z.infer<typeof calcSignalInputSchema>;

export const signalInputSchema = z.object({
  key: signalKeySchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  dataSourceId: objectIdSchema,
  source: z.union([influxSourceSchema, mongoSourceSchema]),
  quantityKey: z.string().max(64),
  storageUnitId: z.string().max(64),
  /** Null = follow the unit systems (doc 05 §2). */
  displayUnitId: z.string().max(64).nullable().default(null),
  decimals: z.number().int().min(0).max(8).nullable().default(null),
  nativeIntervalSec: z.number().min(0.1).max(86_400).default(5),
  valueType: z.enum(['number', 'boolean', 'string']).default('number'),
  range: range.nullable().optional(),
  validRange: range.nullable().optional(),
  transform: z.object({ scale: z.number().default(1), offset: z.number().default(0) }).nullable().optional(),
  links: z.object({
    wellId: objectIdSchema,
    equipmentInstanceId: objectIdSchema.nullable().optional(),
    signalRole: z.string().max(48).nullable().optional(),
  }),
  clientVisible: z.boolean().default(false),
  stateLabels: z.record(z.string().max(16), z.string().max(40)).nullable().optional(),
});
export type SignalInput = z.infer<typeof signalInputSchema>;

export const signalSchema = z.object({
  id: objectIdSchema,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Null for a calculated signal. */
  dataSourceId: objectIdSchema.nullable(),
  /** Where the series lives. Hidden (null) from callers without `signals.manage`. */
  source: z.union([influxSourceSchema, mongoSourceSchema]).nullable(),
  /** Set for a calculated signal; hidden (null) from callers without `signals.manage`. */
  calc: calcSchema.nullable(),
  calculated: z.boolean(),
  quantityKey: z.string(),
  storageUnitId: z.string(),
  displayUnitId: z.string().nullable(),
  /** Display unit resolved for the caller (unit systems, user preferences). */
  resolvedUnitId: z.string(),
  decimals: z.number().nullable(),
  nativeIntervalSec: z.number(),
  valueType: z.enum(['number', 'boolean', 'string']),
  range: z.object({ min: z.number(), max: z.number() }).nullable(),
  validRange: z.object({ min: z.number(), max: z.number() }).nullable(),
  transform: z.object({ scale: z.number(), offset: z.number() }).nullable(),
  links: signalLinksSchema,
  equipmentTag: z.string().nullable(),
  clientVisible: z.boolean(),
  stateLabels: z.record(z.string()).nullable(),
  status: z.enum(['active', 'archived']),
  lastSeenAt: z.string().nullable(),
});
export type Signal = z.infer<typeof signalSchema>;

export const signalListQuerySchema = z.object({
  wellId: objectIdSchema.optional(),
  fieldId: objectIdSchema.optional(),
  equipmentId: objectIdSchema.optional(),
  dataSourceId: objectIdSchema.optional(),
  quantity: z.string().max(64).optional(),
  q: z.string().max(120).optional(),
  status: z.enum(['active', 'archived', 'all']).default('active'),
});
export type SignalListQuery = z.infer<typeof signalListQuerySchema>;

export const bulkSignalsRequestSchema = z.object({ signals: z.array(signalInputSchema).min(1).max(500) });
export const bulkSignalsResponseSchema = z.object({
  created: z.array(z.object({ id: objectIdSchema, key: z.string() })),
  failed: z.array(z.object({ key: z.string(), reason: z.string() })),
});
export type BulkSignalsResponse = z.infer<typeof bulkSignalsResponseSchema>;

// ---- Bindings (doc 06 §1): a concrete signal or an equipment role resolved per well ----
export const bindingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('signal'), signalId: objectIdSchema }),
  z.object({
    kind: z.literal('role'),
    equipmentTypeKey: z.string().max(48),
    signalRole: z.string().max(48),
    /** Always the well; '$wellsite' from before wellsites were removed is read as '$well'. */
    scope: z.preprocess((v) => (v === '$wellsite' ? '$well' : v), z.enum(['$well'])).default('$well'),
  }),
  /** The signal of the well that has this name (case and outer spaces ignored): the same measurement in every well. */
  z.object({ kind: z.literal('name'), name: z.string().trim().min(1).max(120), scope: z.enum(['$well']).default('$well') }),
]);
export type Binding = z.infer<typeof bindingSchema>;

export const resolveRequestSchema = z.object({
  bindings: z
    .array(
      z.object({
        refId: z.string().max(64),
        binding: bindingSchema,
        vars: z.object({ wellId: objectIdSchema.optional() }).default({}),
      }),
    )
    .max(500),
});
export const resolveResponseSchema = z.object({ resolved: z.record(z.string(), objectIdSchema.nullable()) });
export type ResolveRequest = z.infer<typeof resolveRequestSchema>;

// ---- QuerySpec (doc 04 §4.1) ----
export const AGGREGATIONS = ['mean', 'min', 'max', 'last', 'first', 'sum', 'count', 'median', 'spread', 'raw'] as const;
export type Aggregation = (typeof AGGREGATIONS)[number];
export const INTERVAL_STEPS_SEC = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10_800, 21_600, 43_200, 86_400] as const;
export const durationSchema = z.string().regex(/^\d{1,4}(s|m|h|d)$/, 'Use a duration like 30s, 1m, 15m, 1h, 1d');
/** ISO timestamp, "now", or relative "now-12h". */
export const timeExpressionSchema = z.union([z.string().datetime({ offset: true }), z.string().regex(/^now(-\d{1,5}(s|m|h|d|w))?$/)]);

export const querySeriesSchema = z.object({
  refId: z.string().min(1).max(64),
  signalId: objectIdSchema,
  agg: z.enum(AGGREGATIONS).optional(),
  displayUnitId: z.string().max(64).nullable().optional(),
});
export const querySpecSchema = z.object({
  range: z.object({ from: timeExpressionSchema, to: timeExpressionSchema }),
  timeZone: z.string().max(64).optional(),
  interval: z.union([z.literal('auto'), durationSchema]).default('auto'),
  maxPoints: z.number().int().min(10).max(5000).default(1000),
  series: z.array(querySeriesSchema).min(1).max(60),
  fill: z.enum(['none', 'null', 'previous']).default('none'),
});
export type QuerySpec = z.input<typeof querySpecSchema>;
export type ParsedQuerySpec = z.infer<typeof querySpecSchema>;

export const seriesResultSchema = z.object({
  refId: z.string(),
  signalId: z.string(),
  access: z.enum(['ok', 'denied', 'notFound', 'error']),
  error: z.string().optional(),
  storageUnitId: z.string().nullable(),
  displayUnitId: z.string().nullable(),
  /** `[epochMs, value]`; empty for anything but `access: "ok"`. */
  points: z.array(z.tuple([z.number(), z.number().nullable()])),
  truncated: z.boolean().optional(),
});
export type SeriesResult = z.infer<typeof seriesResultSchema>;

export const queryResultSchema = z.object({
  interval: z.string(),
  from: z.string(),
  to: z.string(),
  series: z.array(seriesResultSchema),
  annotations: z.array(z.object({ at: z.string(), text: z.string(), category: z.string(), wellId: z.string(), source: z.enum(['twinlink', 'logger']).optional() })).optional(),
});
export type QueryResult = z.infer<typeof queryResultSchema>;

export const batchQueryRequestSchema = z.object({ queries: z.array(z.object({ key: z.string().max(64), spec: querySpecSchema })).min(1).max(40) });
export const batchQueryResponseSchema = z.object({ results: z.record(z.string(), queryResultSchema) });

export const lastQueryRequestSchema = z.object({
  signalIds: z.array(objectIdSchema).min(1).max(500),
  displayUnits: z.record(objectIdSchema, z.string().max(64)).optional(),
});
export const lastValueSchema = z.object({
  signalId: z.string(),
  access: z.enum(['ok', 'denied', 'notFound', 'error']),
  ts: z.number().nullable(),
  value: z.union([z.number(), z.boolean(), z.string(), z.null()]),
  quality: z.enum(['good', 'stale']).nullable(),
  displayUnitId: z.string().nullable(),
});
export type LastValue = z.infer<typeof lastValueSchema>;
export const lastQueryResponseSchema = z.object({ items: z.array(lastValueSchema) });

export const exportRequestSchema = z.object({
  spec: querySpecSchema,
  format: z.enum(['csv', 'xlsx']).default('csv'),
});

// ---- Units catalog (GET /units) ----
export const unitsCatalogSchema = z.object({
  version: z.number(),
  quantities: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      baseUnitId: z.string(),
      defaultDecimals: z.number(),
      units: z.array(z.object({ id: z.string(), symbol: z.string(), name: z.string(), quantity: z.string(), custom: z.boolean().optional() })),
    }),
  ),
  unitSystems: z.array(z.object({ key: z.string(), name: z.string(), defaults: z.record(z.string()) })),
});

// ---- Background export (doc 04 §5) ----
export const exportJobRequestSchema = z.object({
  spec: querySpecSchema,
  /** One row per interval, whatever the length of the range. */
  interval: durationSchema,
});
export type ExportJobRequest = z.infer<typeof exportJobRequestSchema>;

export const exportJobSchema = z.object({
  id: objectIdSchema,
  status: z.enum(['running', 'done', 'failed']),
  progress: z.number(),
  rows: z.number(),
  signals: z.number(),
  size: z.number(),
  interval: z.string(),
  from: z.string(),
  to: z.string(),
  error: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type ExportJob = z.infer<typeof exportJobSchema>;
